import { getChannelTypeToText } from "@whatslly/types/instant-messaging/messagesEmitter";
import type { MessageChannelType, ReportedMessage } from "@whatslly/types/instant-messaging/messagesEmitter";
import { i18n } from "@whatslly/commons/i18nService";
import { getReportedMessageSenderAndReceiver } from "@whatslly/commons/getReportedMessageSenderAndReceiver";
import { parseTeamsMessage } from "@whatslly/commons/teamsMessageParser";
import type { ComponentMessageType } from "../types/ComponentMessageType";

export type ReportedMessageMedia = Partial<NonNullable<ReportedMessage["media"]>> & { url?: string };

export type ReportedMessageLike = Partial<Omit<ReportedMessage, "media">> & {
  _id?: string;
  messageId?: string;
  sourcePhone?: string;
  media?: ReportedMessageMedia | string;
};

const isTrue = (value: unknown) => String(value) === "true";

// Archive platforms keep the chat in the receiver fields for every group message; WhatsApp puts an incoming one's group in the sender fields
export const isArchiveChannel = (channelType?: MessageChannelType) => channelType === "teams-archive" || channelType === "discord-archive";

// Teams archives the raw HTML body
const textOf = (message: ReportedMessageLike) => (message.channelType === "teams-archive" ? parseTeamsMessage(message.text).plainText : message.text);

const toFile = (message: ReportedMessageLike): ComponentMessageType["file"] => {
  // CSV-read messages carry the file key as a string, empty when there is no file
  const media: ReportedMessageMedia | undefined =
    typeof message.media === "string" ? (message.media ? { fileUrl: message.media } : undefined) : message.media;
  if (!media) return undefined;
  const url = media.url ?? media.fileUrl ?? "";
  return {
    id: media.fileId ?? "",
    fileType: message.type === "ptt" ? "audio" : message.type,
    size: media.size ?? NaN,
    url,
    link: url,
    title: media.fileName ?? "",
    transcribeText: message.transcriptText ? `🎤 ${message.transcriptText}` : textOf(message),
    mimeType: media.mimeType,
    invalid: media.invalid,
  };
};

const toReceiverName = (message: ReportedMessageLike) =>
  message.chatType === "status"
    ? `${getChannelTypeToText(message.channelType ?? "whatsAppWeb")} ${i18n.t("status")}`
    : message.receiverName || message.receiverPhone;

export const toComponentMessage = (message: ReportedMessageLike): ComponentMessageType => {
  const isGroupMsg = isTrue(message.isGroupMsg);
  return {
    id: message.id || message._id || message.messageId || "",
    msgId: message._id || message.id || message.messageId,
    isGroupMsg: String(isGroupMsg),
    isDeleted: isTrue(message.isDeleted),
    isDisappearing: isTrue(message.isDisappearing),
    isEdited: isTrue(message.isEdited),
    isForwarded: isTrue(message.isForwarded),
    senderId: message.senderId,
    senderName: (isGroupMsg ? message.notifyName || message.senderName : message.senderName) || message.senderPhone || "",
    senderPhoneNumber: (isGroupMsg ? message.authorPhone || message.senderPhone : message.senderPhone) ?? "",
    receiverId: message.receiverId,
    receiverName: toReceiverName(message),
    receiverPhoneNumber: message.receiverPhone,
    msg: textOf(message) ?? "",
    isSent: isTrue(message.isSentByMe),
    datetime: Number(message.timestamp) ? new Date(Number(message.timestamp)).toISOString() : "",
    quotedMsgId: message.quotedMsgId,
    notifyName: message.notifyName,
    status: message.ack,
    latestUpdate: message.latestUpdate,
    file: toFile(message),
    userEmail: message.userEmail,
    location: message.location,
    chatType: message.chatType,
    channelType: message.channelType,
  };
};

export const toMessageRow = (message: ReportedMessageLike) => ({
  ...toComponentMessage(message),
  ...getReportedMessageSenderAndReceiver(message),
  type: message.type,
  channelType: message.channelType,
  sourcePhone: message.sourcePhone,
  transcriptText: message.transcriptText,
  timestamp: Number(message.timestamp),
});
export type MessageRow = ReturnType<typeof toMessageRow>;

export const toChat = (message: ReportedMessageLike) => {
  const isGroupMsg = isTrue(message.isGroupMsg);
  const isSent = isTrue(message.isSentByMe);
  if (isGroupMsg) {
    const name = isSent || isArchiveChannel(message.channelType) ? message.receiverName : message.senderName;
    return { name: name || "", phone: undefined, type: "group" as const };
  }
  if (message.chatType === "newsletter" || message.chatType === "broadcast" || message.chatType === "status") {
    return {
      name: (isSent ? message.receiverName : message.senderName) || "",
      phone: undefined,
      type: message.chatType,
    };
  }
  return {
    name: (isSent ? message.receiverName || message.receiverPhone : message.senderName || message.senderPhone) || "",
    phone: isSent ? message.receiverPhone : message.senderPhone,
    type: "chat" as const,
  };
};
